import type { GetCurrentMemberFn } from '@/features/iam'
import type { SnackbarFactory } from '@/features/shell'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { func, required, validationContext } from 'validx'
import type { QuickBooksAccount } from '@/features/bookkeeping/platform-connections/BookkeepingPlatformConnection'
import type { BookkeepingPlatformConnectionStore } from '@/features/bookkeeping/platform-connections/BookkeepingPlatformConnectionStore'

export class BookkeepingAddManualQuickBooksConnectionDialogViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The modal.
   */
  readonly modal = new ModalViewModel()

  /**
   * The company name.
   */
  @observable
  accessor companyName = ''

  /**
   * The company ID.
   */
  @observable
  accessor companyId = ''

  /**
   * The validations for the form.
   */
  validation = validationContext(this, {
    companyName: [
      required('Company name is required'),
      func(
        () =>
          this.companyName.length >= 4 ||
          "Company name can't be less than 4 characters",
      ),
    ],
    companyId: [
      required('Company ID is required'),
      func(
        () =>
          this.companyId.length >= 7 ||
          "company ID can't be less than 7 characters",
      ),
    ],
  })

  constructor(
    private readonly _store: BookkeepingPlatformConnectionStore,
    private readonly _newSnackbar: SnackbarFactory,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _onAction: (
      quickBooksAccount: QuickBooksAccount,
    ) => Promise<unknown>,
  ) {
    this._onAction.bind(this)
  }

  /**
   * The current member.
   */
  @computed
  get currentMember() {
    return this._getCurrentMember()
  }

  /** @inheritdoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritdoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Shows the dialog.
   */
  @action.bound
  show() {
    this.validation.reset()
    this.companyName = ''
    this.companyId = ''

    this.modal.show()
  }

  /**
   * Closes the dialog.
   */
  @action.bound
  close() {
    this.modal.close()
  }

  /**
   * Submits the form to create the QuickBooks account and provide the connection.
   */
  submit = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    try {
      const quickBooksAccount =
        await this._store.createQuickBooksPlatformConnection(
          this.currentMember.workspaceId,
          this.companyId,
          this.companyName,
        )

      this.close()
      await this._onAction(quickBooksAccount)
    } catch (err: unknown) {
      const snackbar = this._newSnackbar()
      snackbar.fromError(err)
    }
  })
}
